// ---------------------------------------------------------------------------
// Person External IDs — Multi-Provider Identity Resolution
// ---------------------------------------------------------------------------
// Maps external system IDs (Shopify, WooCommerce, HubSpot, etc.) to
// internal person records. Used for identity resolution during sync:
// Shopify customer email → emailHash → people.id.
//
// Each row: one person ↔ one provider ↔ one external ID.
// Unique per (tenantId, provider, externalId) — no duplicates.
// GDPR erasure: DELETE WHERE tenant_id = X (standard cascade).
// ---------------------------------------------------------------------------

import { pgTable, uuid, text, jsonb, timestamp, index, uniqueIndex } from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

export const EXTERNAL_ID_PROVIDERS = [
  "shopify", "woocommerce", "hubspot", "salesforce", "zoho",
  "activecampaign", "stripe", "ghl", "facebook", "instagram",
  "google", "tiktok",
] as const;

export type ExternalIdProvider = (typeof EXTERNAL_ID_PROVIDERS)[number];

export const personExternalIds = pgTable(
  "person_external_ids",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    personId: uuid("person_id").notNull(), // FK to people table
    provider: text("provider").notNull(), // ExternalIdProvider
    externalId: text("external_id").notNull(), // Provider's ID for this person
    externalEmail: text("external_email"), // For identity resolution fallback
    metadata: jsonb("metadata").default({}), // Extra provider-specific data
    createdAt: timestamp("created_at", tz).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", tz).defaultNow().notNull(),
  },
  (table) => [
    uniqueIndex("uq_person_ext_ids_provider").on(table.tenantId, table.provider, table.externalId),
    index("idx_person_ext_ids_person").on(table.tenantId, table.personId),
    index("idx_person_ext_ids_email").on(table.tenantId, table.provider, table.externalEmail),
  ],
);
